import { Edit2, Send, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { MessageStatusIcon } from '@/modules/messaging/components/MessageStatusIcon';
import type { MessageApi } from '@/modules/messaging/types';
import { Button } from '@/modules/ui/components/button/Button';
import { daysSince } from '@/utils/dateUtils';

import { EditableMessage } from './EditableMessage';

interface MessageBubbleProps {
  message: MessageApi;
  isPending: boolean;
  isEditing: boolean;
  editingContent: string;
  onSendMessage?: (messageId: string) => void;
  onEditStart: () => void;
  onEditChange: (content: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  onDelete: () => void;
}

export const MessageBubble = ({
  message,
  isPending,
  isEditing,
  editingContent,
  onSendMessage,
  onEditStart,
  onEditChange,
  onEditSave,
  onEditCancel,
  onDelete,
}: MessageBubbleProps) => {
  const actionsVisible =
    message.origin === 'Recruiter' &&
    (message.status === 'Draft' || message.status === 'Failed');

  const isRecruiter = message.origin === 'Recruiter';

  return (
    <div
      className={cn(
        'flex w-full items-start gap-4 py-4',
        isRecruiter ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-2 overflow-hidden rounded-[10px] px-6 py-4 text-sm leading-6 font-normal',
          'max-w-[70%] min-w-[200px]',
          'shadow-[0px_2px_4px_0px_#00000026]',
          isRecruiter ? 'bg-primary/10' : 'bg-card',
        )}
      >
        {isEditing ? (
          <div className="lg:min-w-80">
            <EditableMessage
              content={editingContent}
              isPending={isPending}
              onChange={onEditChange}
              onSave={onEditSave}
              onCancel={onEditCancel}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 lg:min-w-80">
            <p className="text-sm leading-6 font-normal break-words whitespace-pre-line">
              {message.content}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {actionsVisible ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isPending}
                      onClick={() => onSendMessage?.(message.id)}
                      className="h-6 w-6 p-0"
                    >
                      <Send size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isPending}
                      onClick={onEditStart}
                      className="h-6 w-6 p-0"
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isPending}
                      onClick={onDelete}
                      className="h-6 w-6 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </>
                ) : (
                  <div />
                )}
              </div>

              {message.sentAt && (
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground text-[0.75em] opacity-80">
                    {daysSince(message.sentAt)}
                  </span>
                  <MessageStatusIcon status={message.status} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
